import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { IOutlet } from '@cakeiteasy/web-utils';
import type * as MapLibre from 'maplibre-gl';
import {
  configureMapLibreWorker,
  getMapLibreOutletMarkerHtml,
  hasCoordinates,
  latLng,
  simplifyMapLibreOutletStyle,
} from '../../../helpers/maplibre.helper';
import { DEFAULT_MAPLIBRE_CONFIG } from '../../../constants/maplibre.constants';
import { MapMarkerThemeService } from '../../../services/map-marker-theme.service';

@Component({
  standalone: true,
  selector: 'ui-accordion-outlet-map',
  templateUrl: './ui-accordion-outlet-map.component.html',
  styleUrls: ['./ui-accordion-outlet-map.component.scss'],
})
export class UiAccordionOutletMapComponent implements AfterViewInit, OnDestroy {
  readonly outlet = input.required<IOutlet>();

  private readonly _mapContainer = viewChild.required<ElementRef<HTMLDivElement>>('mapContainer');

  private readonly _markerTheme = inject(MapMarkerThemeService);
  private readonly _platformId = inject(PLATFORM_ID);

  private _map: MapLibre.Map;
  private _isDestroyed = false;

  async ngAfterViewInit(): Promise<void> {
    const outlet = this.outlet();

    if (!isPlatformBrowser(this._platformId) || !hasCoordinates(outlet)) {
      return;
    }

    const maplibre = await import('maplibre-gl');

    if (this._isDestroyed) {
      return;
    }

    configureMapLibreWorker(maplibre);
    const { styleUrl, maxZoom } = DEFAULT_MAPLIBRE_CONFIG;
    const position = latLng(outlet as { latitude: number; longitude: number });

    this._map = new maplibre.Map({
      container: this._mapContainer().nativeElement,
      style: styleUrl,
      center: position,
      zoom: DEFAULT_MAPLIBRE_CONFIG.initialZoom,
      maxZoom,
      scrollZoom: false,
      attributionControl: { compact: true },
    });
    simplifyMapLibreOutletStyle(this._map);

    this._map.addControl(new maplibre.NavigationControl({ showCompass: false }), 'bottom-left');

    new maplibre.Marker({ element: this._createMarkerElement(outlet), anchor: 'center' })
      .setLngLat(position)
      .addTo(this._map);
  }

  ngOnDestroy(): void {
    this._isDestroyed = true;
    this._map?.remove();
  }

  private _createMarkerElement(outlet: IOutlet): HTMLElement {
    const element = document.createElement('div');
    element.title = outlet.name;
    element.innerHTML = getMapLibreOutletMarkerHtml(outlet.name, this._markerTheme.getMarkerOptions(true));

    return element;
  }
}
